import { getTenantId } from '@/utils/tenant'

export type ExplainVideoStatus = 'RESERVED' | 'UPLOADING' | 'PROCESSING' | 'READY' | 'FAILED' | 'DELETED' | 'EXPIRED'

export interface ExplainVideoView {
  id: string
  status: ExplainVideoStatus
  playbackUrl: string | null
  fileName: string | null
  sizeBytes: number | null
  durationMillis: number | null
  width: number | null
  height: number | null
  errorCode: string | null
  createdAt: string
  version: number
  expiresAt?: string | null
}

export interface ExplainVideoCapability {
  enabled: boolean
  reason: string | null
  maxVideos: number
  currentCount: number
  maxBytes: number
  partSize: number
}

export interface ExplainVideoReservation {
  id: string
  uploadToken: string
  expiresAt: string
  maxBytes: number
  partSize: number
}

export interface ExplainVideoPart {
  partNumber: number
  eTag: string
  sizeBytes: number
}

export class ExplainVideoError extends Error {
  constructor(public readonly code: string, public readonly status: number) {
    super(code)
    this.name = 'ExplainVideoError'
  }
}

const BASE_PATH = '/lc/learning/explain-photo/videos'
const API_BASE = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '')

/** Public QR/session requests intentionally bypass login-refresh and URL logging interceptors. */
async function request<T>(path: string, init: RequestInit = {}, uploadToken?: string): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')
  const tenantId = getTenantId()
  if (tenantId) headers.set('X-Tenant-Id', tenantId)
  if (uploadToken) headers.set('X-Upload-Token', uploadToken)
  const response = await fetch(`${API_BASE}${BASE_PATH}${path}`, { ...init, headers, credentials: 'include' })
  const body: { data?: T; error?: { code?: string } } = await response.json()
  if (!response.ok || body.data === undefined) {
    throw new ExplainVideoError(body.error?.code ?? 'VIDEO_REQUEST_FAILED', response.status)
  }
  return body.data
}

export function getExplainVideoCapability(token: string): Promise<ExplainVideoCapability> {
  return request(`/capability?token=${encodeURIComponent(token)}`)
}

export function listExplainVideos(token: string): Promise<ExplainVideoView[]> {
  return request(`?token=${encodeURIComponent(token)}`)
}

export function createExplainVideoReservation(capability: ExplainVideoCapability): ExplainVideoReservation {
  if (!globalThis.crypto?.randomUUID || !globalThis.crypto?.getRandomValues) throw new ExplainVideoError('VIDEO_CAPTURE_UNAVAILABLE', 0)
  const uploadToken = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  return { id: crypto.randomUUID(), uploadToken, expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), maxBytes: capability.maxBytes, partSize: capability.partSize }
}

export function reserveExplainVideo(token: string, proposed: ExplainVideoReservation): Promise<ExplainVideoReservation> {
  return request('/reservations', { method: 'POST', body: JSON.stringify({ token, clientReservationId: proposed.id, clientUploadToken: proposed.uploadToken }) })
}

export function getExplainVideo(session: ExplainVideoReservation): Promise<ExplainVideoView> {
  return request(`/${session.id}`, {}, session.uploadToken)
}

export function startExplainVideoUpload(session: ExplainVideoReservation, file: File, dimensions?: { width: number; height: number }): Promise<ExplainVideoView> {
  return request(`/${session.id}/upload`, {
    method: 'POST',
    body: JSON.stringify({ fileName: file.name, contentType: resolveVideoContentType(file), sizeBytes: file.size, ...dimensions }),
  }, session.uploadToken)
}

export function listExplainVideoParts(session: ExplainVideoReservation): Promise<{ parts: ExplainVideoPart[] }> {
  return request(`/${session.id}/parts`, {}, session.uploadToken)
}

export function getExplainVideoPartUrl(session: ExplainVideoReservation, partNumber: number): Promise<{ uploadUrl: string; expiresAt: string }> {
  return request(`/${session.id}/part-url`, { method: 'POST', body: JSON.stringify({ partNumber }) }, session.uploadToken)
}

export function completeExplainVideoUpload(session: ExplainVideoReservation): Promise<ExplainVideoView> {
  return request(`/${session.id}/complete`, { method: 'POST' }, session.uploadToken)
}

export function retryExplainVideo(session: ExplainVideoReservation): Promise<ExplainVideoView> {
  return request(`/${session.id}/retry`, { method: 'POST' }, session.uploadToken)
}

export function deleteExplainVideo(id: string, token: string, session?: ExplainVideoReservation): Promise<{ deleted: boolean }> {
  return request(`/${id}${session ? '' : `?token=${encodeURIComponent(token)}`}`, { method: 'DELETE' }, session?.uploadToken)
}

const VIDEO_TYPES = new Set(['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'])
const VIDEO_EXTENSIONS: Record<string, string> = { mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm', m4v: 'video/x-m4v' }

export function resolveVideoContentType(file: File): string | null {
  const type = file.type.toLowerCase().split(';')[0].trim()
  if (VIDEO_TYPES.has(type)) return type
  if (type) return null
  return VIDEO_EXTENSIONS[file.name.split('.').pop()?.toLowerCase() ?? ''] ?? null
}

function abortError(): DOMException {
  return new DOMException('Aborted', 'AbortError')
}

function waitForRetry(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const cancel = () => { clearTimeout(timer); reject(abortError()) }
    const timer = setTimeout(() => { signal.removeEventListener('abort', cancel); resolve() }, ms)
    signal.addEventListener('abort', cancel, { once: true })
    if (signal.aborted) cancel()
  })
}

/** S3 ListParts is authoritative: completion never depends on CORS exposing ETag. */
export async function uploadExplainVideoParts(
  session: ExplainVideoReservation,
  file: File,
  signal: AbortSignal,
  onProgress: (uploadedBytes: number) => void,
  dimensions?: { width: number; height: number },
  onSessionUpdate?: (session: ExplainVideoReservation) => void,
): Promise<ExplainVideoView> {
  if (file.size > session.maxBytes || !resolveVideoContentType(file)) {
    throw new ExplainVideoError(file.size > session.maxBytes ? 'VIDEO_TOO_LARGE' : 'VIDEO_UNSUPPORTED_TYPE', 400)
  }
  if (signal.aborted) throw abortError()
  const initial = await startExplainVideoUpload(session, file, dimensions)
  if (initial.expiresAt) onSessionUpdate?.({ ...session, expiresAt: initial.expiresAt })
  if (initial.status === 'PROCESSING' || initial.status === 'READY') return initial
  const { parts } = await listExplainVideoParts(session)
  const uploaded = new Map(parts.map(part => [part.partNumber, part.sizeBytes]))
  let completedBytes = 0
  for (let offset = 0, partNumber = 1; offset < file.size; offset += session.partSize, partNumber += 1) {
    const part = file.slice(offset, Math.min(offset + session.partSize, file.size))
    if (uploaded.get(partNumber) !== part.size) {
      let succeeded = false
      for (let attempt = 0; attempt < 4; attempt += 1) {
        if (signal.aborted) throw abortError()
        try {
          const { uploadUrl } = await getExplainVideoPartUrl(session, partNumber)
          const result = await fetch(uploadUrl, { method: 'PUT', body: part, signal })
          if (!result.ok) throw new ExplainVideoError('VIDEO_PART_UPLOAD_FAILED', result.status)
          succeeded = true
          break
        } catch (failure) {
          if (signal.aborted || (failure instanceof DOMException && failure.name === 'AbortError')) throw abortError()
          if (attempt === 3) throw failure
          await waitForRetry(500 * 2 ** attempt, signal)
        }
      }
      if (!succeeded) throw new ExplainVideoError('VIDEO_PART_UPLOAD_FAILED', 0)
    }
    completedBytes += part.size
    onProgress(completedBytes)
  }
  if (signal.aborted) throw abortError()
  return completeExplainVideoUpload(session)
}
